import { Helmet } from 'react-helmet-async';
import type { ReactNode } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { buildPageTitle, DEFAULT_DESCRIPTION, SITE_URL } from '@/config/seo';

interface PageShellProps {
  title: string;
  description?: string;
  path: string;
  children: ReactNode;
  /**
   * Hide the global Header — for pages that render their own navigation
   * inside a hero (e.g. the home page).
   */
  hideHeader?: boolean;
}

export function PageShell({
  title,
  description,
  path,
  children,
  hideHeader = false,
}: PageShellProps) {
  const fullTitle = buildPageTitle(title);
  const desc = description ?? DEFAULT_DESCRIPTION;
  const canonical = `${SITE_URL}${path}`;
  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <a
        href="#main-content"
        className="sr-only-focusable absolute left-4 top-4 z-50 rounded-lg bg-clearlab-blue px-4 py-2 text-white"
      >
        Skip to main content
      </a>
      <div className="flex min-h-screen flex-col">
        {hideHeader ? null : <Header />}
        <main id="main-content" tabIndex={-1} className="flex-1">
          {children}
        </main>
        <Footer />
      </div>
    </>
  );
}
